import { setBadgeCount, setHidden } from './controllerDom.js';
import { state as controllerState } from './controllerState.js';
import type { HostEventLogEntryPayload } from '../shared/types.js';

interface TechTreeRaw {
  id: string;
  sym: string;
  name: string;
  rarity: 'common' | 'uncommon' | 'rare';
  desc: string;
}

interface TechTreePill {
  name: string;
  type: 'raw' | 'refined' | 'product' | 'catalyst';
}

interface TechTreeStep {
  inputs: TechTreePill[];
  outputs: TechTreePill[];
}

interface TechTreeChain {
  id: string;
  label: string;
  steps: TechTreeStep[];
}

interface TechTreeData {
  rawMaterials: TechTreeRaw[];
  chains: TechTreeChain[];
}

let controllerHostEventLogEntries: HostEventLogEntryPayload[] = [];
let unreadHostEventLogCount = 0;
let techTreeDataCache: TechTreeData | null = null;

const renderHostEventLogEntries = (list: HTMLUListElement | null): void => {
  if (!list) return;
  list.innerHTML = controllerHostEventLogEntries.map(entry => `<li>${entry.stamp} - ${entry.message}</li>`).join('');
};

const setHostEventLogUnreadCount = (
  badge: HTMLElement | null,
  button: HTMLButtonElement | null,
  count: number
): void => {
  unreadHostEventLogCount = count;
  setBadgeCount(badge, count);
  button?.classList.toggle('has-unread', count > 0);
};

export const showQuickToast = (toast: HTMLElement | null, message: string): void => {
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  toast.classList.add('controller-quick-toast-show');

  if (controllerState.quickToastTimeout !== null) {
    window.clearTimeout(controllerState.quickToastTimeout);
  }

  controllerState.quickToastTimeout = window.setTimeout(() => {
    if (!toast) {
      controllerState.quickToastTimeout = null;
      return;
    }

    toast.classList.remove('controller-quick-toast-show');
    toast.hidden = true;
    controllerState.quickToastTimeout = null;
  }, 1400);
};

export const closeEventLogPanel = (panel: HTMLElement | null): void => {
  setHidden(panel, true);
};

export const openEventLogPanel = (
  panel: HTMLElement | null,
  list: HTMLUListElement | null,
  button: HTMLButtonElement | null
): void => {
  setHidden(panel, false);
  renderHostEventLogEntries(list);
  setHostEventLogUnreadCount(null, button, 0);
  button?.classList.remove('flash');
};

export const appendControllerHostEventLogEntry = (
  payload: HostEventLogEntryPayload,
  panel: HTMLElement | null,
  list: HTMLUListElement | null,
  badge: HTMLElement | null,
  button: HTMLButtonElement | null
): void => {
  controllerHostEventLogEntries.unshift(payload);
  if (controllerHostEventLogEntries.length > 20) {
    controllerHostEventLogEntries.length = 20;
  }

  if (panel && !panel.hidden) {
    renderHostEventLogEntries(list);
    return;
  }

  setHostEventLogUnreadCount(badge, button, unreadHostEventLogCount + 1);
  if (button) {
    button.classList.add('flash');
    window.setTimeout(() => button?.classList.remove('flash'), 1200);
  }
};

const renderTechTreePanel = (
  body: HTMLElement | null,
  data: TechTreeData
): void => {
  if (!body) return;

  body.innerHTML = '';

  const rarities: Array<{ key: TechTreeRaw['rarity']; label: string }> = [
    { key: 'common', label: 'Common raw materials' },
    { key: 'uncommon', label: 'Uncommon raw materials' },
    { key: 'rare', label: 'Rare raw materials' }
  ];

  for (const { key, label } of rarities) {
    const materials = data.rawMaterials.filter(m => m.rarity === key);
    if (materials.length === 0) continue;

    const sectionTitle = document.createElement('div');
    sectionTitle.className = 'ttr-section-title';
    sectionTitle.textContent = label;
    body.appendChild(sectionTitle);

    const row = document.createElement('div');
    row.className = 'ttr-rarity-row';

    for (const mat of materials) {
      const item = document.createElement('div');
      item.className = `ttr-raw ${mat.rarity}`;

      const sym = document.createElement('span');
      sym.className = 'ttr-sym';
      sym.textContent = mat.sym;
      item.appendChild(sym);

      const info = document.createElement('div');
      info.className = 'ttr-raw-info';

      const name = document.createElement('div');
      name.className = 'ttr-raw-name';
      name.textContent = mat.name;
      info.appendChild(name);

      const desc = document.createElement('div');
      desc.className = 'ttr-raw-desc';
      desc.textContent = mat.desc;
      info.appendChild(desc);

      item.appendChild(info);
      row.appendChild(item);
    }

    body.appendChild(row);
  }

  const chainsTitle = document.createElement('div');
  chainsTitle.className = 'ttr-section-title';
  chainsTitle.textContent = 'Refinement chains';
  body.appendChild(chainsTitle);

  for (const chain of data.chains) {
    const chainBlock = document.createElement('div');
    chainBlock.className = 'ttr-chain';

    const chainLabel = document.createElement('div');
    chainLabel.className = 'ttr-chain-label';
    chainLabel.textContent = chain.label;
    chainBlock.appendChild(chainLabel);

    for (const step of chain.steps) {
      const stepRow = document.createElement('div');
      stepRow.className = 'ttr-step';

      let first = true;
      for (const input of step.inputs) {
        if (!first) {
          const plus = document.createElement('span');
          plus.className = 'ttr-plus';
          plus.textContent = '+';
          stepRow.appendChild(plus);
        }
        first = false;

        const pill = document.createElement('span');
        pill.className = `ttr-pill ${input.type}`;
        pill.textContent = input.name;
        stepRow.appendChild(pill);
      }

      const arrow = document.createElement('span');
      arrow.className = 'ttr-arrow';
      arrow.textContent = '→';
      stepRow.appendChild(arrow);

      let firstOut = true;
      for (const output of step.outputs) {
        if (!firstOut) {
          const plus = document.createElement('span');
          plus.className = 'ttr-plus';
          plus.textContent = '+';
          stepRow.appendChild(plus);
        }
        firstOut = false;

        const pill = document.createElement('span');
        pill.className = `ttr-pill ${output.type}`;
        pill.textContent = output.name;
        stepRow.appendChild(pill);
      }

      chainBlock.appendChild(stepRow);
    }

    body.appendChild(chainBlock);
  }
};

export const closeTechTreePanel = (panel: HTMLElement | null): void => {
  setHidden(panel, true);
};

export const openTechTreePanel = (
  panel: HTMLElement | null,
  body: HTMLElement | null
): void => {
  if (!panel) return;
  setHidden(panel, false);

  if (techTreeDataCache) {
    renderTechTreePanel(body, techTreeDataCache);
    return;
  }

  if (body) {
    body.innerHTML = '<div class="ttr-loading">Loading…</div>';
  }

  fetch('/config/tech-tree-data.json')
    .then(res => res.json() as Promise<TechTreeData>)
    .then(data => {
      techTreeDataCache = data;
      renderTechTreePanel(body, data);
    })
    .catch(() => {
      if (body) {
        body.innerHTML = '<div class="ttr-loading">Failed to load tech tree data.</div>';
      }
    });
};
